import { useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useListVenues } from '@cocoonspace/sdk-js/v2/domains/venues/venues'
import { Button } from '@cocoonspace/ui/components/button'
import { Card } from '@cocoonspace/ui/components/card'
import { DialogTitle, DialogHeader } from '@cocoonspace/ui/components/dialog'
import { FormField } from '@cocoonspace/ui/components/form'
import { Skeleton } from '@cocoonspace/ui/components/skeleton'
import { Tabs, TabsList, TabsTrigger } from '@cocoonspace/ui/components/tabs'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { keepPreviousData } from '@tanstack/react-query'
import { MapPinIcon } from 'lucide-react'
import { LocalizedLink as Link } from '~/components/localized-link'

import { CheckStateIcon } from '~/components/check-state-icon'
import type { LocationCocoonSchema } from '../../event-type-form'

const ZoneTabs = ({
	value,
	onChange,
}: {
	value: 'paris' | 'lyon'
	onChange: (value: 'paris' | 'lyon') => void
}) => (
	<Tabs
		value={value}
		className='p-4 px-6 pb-0'
		onValueChange={(value) => onChange(value as 'paris' | 'lyon')}
	>
		<TabsList className='border !shadow-none'>
			<TabsTrigger value='paris'>Paris</TabsTrigger>
			<TabsTrigger value='lyon'>Lyon</TabsTrigger>
		</TabsList>
	</Tabs>
)

const Loader = () =>
	Array.from({ length: 6 }).map((_, i) => (
		<Card
			key={i}
			variant='outline'
			className='flex w-full items-center gap-4 p-4 py-2'
		>
			<div className='flex flex-1 flex-col gap-2'>
				<Skeleton className='h-4 w-[120px]' />
				<Skeleton className='h-3 w-[180px]' />
			</div>

			<div className='flex flex-row items-center gap-4'>
				<Skeleton className='h-4 w-[120px]' />
				<Skeleton className='h-[32px] w-[113px]' />
			</div>
		</Card>
	))

const VenueItem = ({
	venue,
	value,
	onChange,
}: {
	venue: any // Venue
	value: string
	onChange: (value: string) => void
}) => {
	const { t } = useLingui()

	const address = useMemo(() => {
		const { address1, postal_code, city } = (venue as any) ?? {}

		return `${address1 || ''}, ${postal_code || ''} ${city || ''}`
	}, [venue])

	return (
		<button
			type='button'
			className={cn(
				'hover:border-primary flex w-full flex-col rounded-lg border text-left',
				{ 'border-primary': value === venue.id },
			)}
			onClick={() => onChange(venue.id)}
		>
			<div className='flex flex-col gap-4 px-4 py-2 md:flex-row md:items-center'>
				<div className='flex items-center gap-2'>
					<CheckStateIcon isActive={value === venue.id} />
				</div>

				<div className='flex-1'>
					<div className='text-sm font-bold'>{venue.name}</div>

					<div className='text-muted-foreground flex flex-row items-center gap-1 text-xs'>
						<MapPinIcon />
						{address}
					</div>
				</div>

				<div className='flex shrink-0 items-center gap-2 max-md:grid max-md:grid-cols-2'>
					<Button
						size='sm'
						variant='ghost'
						onClick={(e) => e.stopPropagation()}
						asChild
					>
						<Link
							href={`/venues/${venue.slug}`}
							target='_blank'
						>
							{t`Voir les Cocoons`}
						</Link>
					</Button>
				</div>
			</div>
		</button>
	)
}

export const StepVenues = () => {
	const { t } = useLingui()
	const [zone, setZone] = useState<'paris' | 'lyon'>('paris')

	const { setValue } = useFormContext<LocationCocoonSchema>()

	const { data: venues, isLoading } = useListVenues(
		{
			zone,
			offer: 'cocoon',
			parentId: 'root',
			sortBy: 'asc(title)',
			status: 'active',
			// TODO: the api should be fixed to have pageSize as a number not a string
			pageSize: '1000',
		},
		{ query: { placeholderData: keepPreviousData } },
	)

	return (
		<>
			<DialogHeader className='p-6 pb-0'>
				<DialogTitle>{t`Sélectionnez une adresse`}</DialogTitle>
			</DialogHeader>

			<div className='flex flex-1 flex-col gap-4 overflow-hidden'>
				<ZoneTabs
					value={zone}
					onChange={(value) => setZone(value)}
				/>

				<div className='flex w-full flex-1 flex-col gap-2 overflow-y-auto px-6 pb-6'>
					{isLoading && <Loader />}

					{venues?.data?.map((venue) => (
						<FormField
							key={venue.id}
							name='venue_id'
							render={({ field }) => (
								<VenueItem
									venue={venue}
									value={field.value}
									onChange={(value) => {
										field.onChange(value)

										setValue('space_ids', [], {
											shouldDirty: true,
											shouldValidate: true,
										})
									}}
								/>
							)}
						/>
					))}
				</div>
			</div>
		</>
	)
}
